

import { registerROQDemoController } from '../components/roq-demo-controller.js'
import { VideoRecorder } from '../utils/VideoRecorder.js'
import { getClientAssetConfig } from '../config/getClientAssetConfig.js'
import { isIOS, isIOSSafari, isIOSChrome } from '../utils/deviceDetection.js'
import { CharacterConfig } from '../types/index.js'

/**
 * AR Engine Manager for A-Frame AR scene management with MindAR.js
 * Handles image-based AR tracking, video content, and animations
 * Requirements: 3.1, 4.2, 4.3, 4.4, 4.5
 */
export class AREngine {
  private scene: HTMLElement | null = null
  // Multi-target support: one entity per target index
  private imageTargets: HTMLElement[] = []
  private arContents: HTMLElement[] = []
  // Active arContent (switches when targetFound fires for a new index)
  private arContent: HTMLElement | null = null
  private model: HTMLElement | null = null
  private cta3D: HTMLElement | null = null
  private videoElement: HTMLVideoElement | null = null
  private isInitialized = false
  private isModelLoaded = false
  private animationState: 'stopped' | 'playing' | 'paused' | 'completed' = 'stopped'
  private clientAssetConfig: ReturnType<typeof getClientAssetConfig>
  private animationTimeout: number | null = null
  private videoRecorder: VideoRecorder

  // Target detection state (managed by ROQ demo controller)
  private isTargetDetected = false

  // Animation state tracking for resume functionality
  private wasAnimationCompleteBeforeLoss = false
  private wereCTAsVisibleBeforeLoss = false

  private hasAudioUnlocked = false
  private soundPromptElement: HTMLElement | null = null
  private pendingAudioUnlockHandler: ((event: Event) => void) | null = null
  private audioContext: AudioContext | null = null
  private boostedAudioSource: MediaElementAudioSourceNode | null = null
  private boostedGainNode: GainNode | null = null

  // Current character for multi-target collections
  private currentCharacter: CharacterConfig | null = null
  // Separate background audio element (for collections with mp3 + video)
  private audioElement: HTMLAudioElement | null = null

  private onMarkerFoundCallback?: (targetIndex: number) => void
  private onMarkerLostCallback?: () => void
  private onAnimationCompleteCallback?: () => void

  // Container element the a-scene is appended to (stored for MindAR cleanup)
  private container: HTMLElement | null = null

  /** When set, overrides clientAssetConfig.imageTargetsUrl for the MindAR scene.
   *  Used to point MindAR at a per-character .mind file after manual selection. */
  private imageTargetOverride?: string

  constructor(clientId?: string, imageTargetOverride?: string) {
    this.clientAssetConfig = getClientAssetConfig(clientId)
    this.imageTargetOverride = imageTargetOverride
    console.log('AREngine initialized with MindAR.js support', clientId ? `(client: ${clientId})` : '', imageTargetOverride ? `(target override: ${imageTargetOverride})` : '')

    // Initialize video recorder (Phase 2)
    this.videoRecorder = new VideoRecorder()

    // Register the ROQ demo controller component
    this.registerROQDemoComponent()
  } 
  

  /**
   * Register the ROQ demo controller A-Frame component
   * Requirements: 3.1, 3.2, 4.1, 4.2
   */
  private registerROQDemoComponent(): void {
    try {
      registerROQDemoController()
      console.log('ROQ Demo Controller component registered')
    } catch (error) {
      console.error('Failed to register ROQ Demo Controller:', error)
    }
  }

  /**
   * Initialize A-Frame AR scene with MindAR image tracking
   * Requirements: 3.1, 4.2
   */
  async initializeScene(container: HTMLElement): Promise<void> {
    if (this.isInitialized) {
      console.warn('AR scene already initialized')
      return
    }

    try {
      console.log('Initializing A-Frame AR scene with MindAR...')

      // Check prerequisites
      await this.checkARPrerequisites()

      // Create A-Frame scene with MindAR configuration and ROQ demo controller
      this.scene = document.createElement('a-scene')
      
      // Set up scene attributes with error handling
      try {
        // Optimized MindAR configuration for fast, responsive scanning
        // filterMinCF: Lower = more sensitive/faster detection (0.0001 is very sensitive)
        // filterBeta: Lower = smoother tracking but can be slower (0.001 is good balance)
        // Verify client-specific image target exists before setting MindAR attribute.
        // If the target file is missing (common when only ROQ target is present),
        // fall back to the ROQ compiled target so the camera can still initialize.
        // Per-character override takes priority over the shared client-level .mind file
        const requestedImageTarget = this.imageTargetOverride ?? this.clientAssetConfig.imageTargetsUrl
        const fallbackImageTarget = '/assets/compiled-image-targets/roq-target.mind'
        const verifiedSrc = await this.verifyImageTargetExists(requestedImageTarget, fallbackImageTarget)
        // Append a timestamp cache-buster so MindAR always fetches the correct
        // .mind file fresh and never serves a previously cached character's data.
        const imageTargetSrc = `${verifiedSrc}?v=${Date.now()}`
        // autoStart: false prevents A-Frame's play() from calling _startAR immediately.
        // Without this, _startAR fires before start() has created the controller,
        // causing 'Cannot read properties of undefined (reading dummyRun)' crash.
        // We manually await start() in ensureMindARStarted instead.
        this.scene.setAttribute('mindar-image', `imageTargetSrc: ${imageTargetSrc}; autoStart: false; maxTrack: 1; filterMinCF: 0.00005; filterBeta: 0.01; warmupTolerance: 8; missTolerance: 8; uiScanning: no;`)
        this.scene.setAttribute('color-space', 'sRGB')
        // Do NOT set alpha:true — MindAR renders the camera feed as an opaque
        // THREE.js background directly to the WebGL canvas.  With alpha:true the
        // canvas clear-color is rgba(0,0,0,0) which causes the background plane
        // to composite against transparent black, leaving the screen black.
        // MindAR's own examples never enable alpha on the renderer.
        this.scene.setAttribute('renderer', 'colorManagement: true; physicallyCorrectLights: true')
        this.scene.setAttribute('vr-mode-ui', 'enabled: false')
        this.scene.setAttribute('device-orientation-permission-ui', 'enabled: false')
        this.scene.setAttribute('embedded', 'true')
        this.scene.setAttribute('roq-demo-controller', 'targetIndex: 0; guidanceTimeout: 5000; analyticsEnabled: true')
      } catch (error) {
        throw new Error(`Failed to configure A-Frame scene attributes: ${error}`)
      }

      // Set up scene styling with mobile optimizations
      this.scene.style.position = 'fixed'
      this.scene.style.top = '0'
      this.scene.style.left = '0'
      this.scene.style.width = '100vw'
      this.scene.style.height = '100vh'
      this.scene.style.zIndex = '1'
      this.scene.style.pointerEvents = 'auto'
      
      // Mobile performance optimizations
      this.scene.style.webkitTransform = 'translateZ(0)'
      this.scene.style.transform = 'translateZ(0)'
      this.scene.style.webkitBackfaceVisibility = 'hidden'
      this.scene.style.backfaceVisibility = 'hidden'
      this.scene.style.touchAction = 'manipulation'

      // Create MindAR camera with error handling
      try {
        const camera = document.createElement('a-camera')
        camera.setAttribute('position', '0 0 0')
        camera.setAttribute('look-controls', 'enabled: false')
        camera.setAttribute('cursor', 'fuse: false; rayOrigin: mouse;')
        camera.setAttribute('raycaster', 'far: 10000; objects: .clickable')
        this.scene.appendChild(camera)
      } catch (error) {
        throw new Error(`Failed to create MindAR camera: ${error}`)
      }

      // Create MindAR image target anchors — one per character for multi-target collections.
      // When a per-character .mind file override is active the file only contains a single
      // target, so we must only create one anchor regardless of how many characters the
      // collection defines.  Creating more anchors than targets in the .mind file causes
      // MindAR to throw "target index out of bounds" and the whole scene fails to start.
      try {
        const numTargets = this.imageTargetOverride ? 1 : (this.clientAssetConfig.characters?.length || 1)

        for (let i = 0; i < numTargets; i++) {
          const imageTarget = document.createElement('a-entity')
          imageTarget.setAttribute('mindar-image-target', `targetIndex: ${i}`)
          imageTarget.setAttribute('id', `mindar-image-target-${i}`)

          const arContent = document.createElement('a-entity')
          arContent.setAttribute('id', `ar-content-${i}`)
          arContent.setAttribute('visible', 'false')
          imageTarget.appendChild(arContent)

          this.scene.appendChild(imageTarget)
          this.imageTargets.push(imageTarget)
          this.arContents.push(arContent)
        }

        // Default active target is index 0
        this.arContent = this.arContents[0]
      } catch (error) {
        throw new Error(`Failed to create MindAR image targets: ${error}`)
      }

      // Set up MindAR event listeners with error handling
      try {
        this.setupTargetEvents()
      } catch (error) {
        throw new Error(`Failed to setup MindAR event listeners: ${error}`)
      }

      // Add scene to container with error handling
      try {
        this.container = container
        container.appendChild(this.scene)
      } catch (error) {
        throw new Error(`Failed to add AR scene to container: ${error}`)
      }

      // Wait for A-Frame to initialize with timeout
      try {
        await this.waitForAFrameInit()
      } catch (error) {
        throw new Error(`A-Frame initialization timed out: ${error}`)
      }

      // Explicitly start MindAR system and verify camera feed.
      // In some browser/device combinations the scene loads but the
      // MindAR camera stream does not auto-start, resulting in a black view.
      try {
        await this.ensureMindARStarted()
      } catch (error) {
        throw new Error(`MindAR camera startup failed: ${error}`)
      }

      // Set up recording canvas (Phase 2)
      this.setupRecordingCanvas()

      this.isInitialized = true
      console.log('A-Frame AR scene with MindAR initialized successfully')

    } catch (error) {
      console.error('Failed to initialize AR scene:', error)
      
      // Clean up any partial initialization
      this.cleanup()
      
      // Re-throw with more specific error information
      if (error instanceof Error) {
        throw new Error(`AR scene initialization failed: ${error.message}`)
      } else {
        throw new Error(`AR scene initialization failed: ${error}`)
      }
    }
  }

  /**
   * Verify that the provided image target file exists on the server.
   * Returns the original URL if reachable, otherwise returns the provided fallback.
   */
  private async verifyImageTargetExists(url: string, fallback: string): Promise<string> {
    if (!url) return fallback
    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 3000)
      const resp = await fetch(url, { method: 'GET', cache: 'no-store', signal: controller.signal })
      clearTimeout(timeout)
      if (resp.ok) {
        console.log(`Image target verified: ${url}`)
        return url
      }
      console.warn(`Image target not reachable (${resp.status}) - falling back to ${fallback}`)
    } catch (error) {
      console.warn(`Image target fetch failed for ${url} — using fallback ${fallback}:`, error)
    }
    return fallback
  }

  /**
   * Set up MindAR image target detection event listeners with ROQ demo controller
   * Requirements: 3.1, 3.2, 4.1, 4.2
   */
  private setupTargetEvents(): void {
    if (!this.scene) return

    // Listen for ROQ demo controller events instead of direct MindAR events
    this.scene.addEventListener('roq-target-found', (event: Event) => {
      const customEvent = event as CustomEvent
      const targetIndex = customEvent.detail?.targetIndex ?? 0
      console.log('🎯 AREngine: ROQ target found via demo controller! Target index:', targetIndex)

      // Switch active arContent to the newly found target, hiding others
      if (this.arContents.length > 1) {
        this.arContents.forEach((c, i) => {
          if (i !== targetIndex) c.setAttribute('visible', 'false')
        })
      }
      if (this.arContents[targetIndex]) {
        this.arContent = this.arContents[targetIndex]
      }

      // Update target detection state
      this.isTargetDetected = true

      if (this.arContent) {
        this.arContent.setAttribute('visible', 'true')
        console.log('✅ AR content set to visible for target', targetIndex)
      }

      // Handle animation resume if it was paused
      this.handleTargetFoundAnimationState()

      // Call the marker found callback with targetIndex so the controller
      // can resolve the correct character for multi-target collections
      if (this.onMarkerFoundCallback) {
        this.onMarkerFoundCallback(targetIndex)
      }
    })

    this.scene.addEventListener('roq-target-lost', (event: Event) => {
      const customEvent = event as CustomEvent
      const targetIndex = customEvent.detail?.targetIndex ?? 0
      console.log('❌ AREngine: ROQ target lost via demo controller. Target index:', targetIndex)
      
      // Save current animation and CTA state before hiding
      this.saveAnimationStateBeforeLoss()
      
      // Update target detection state
      this.isTargetDetected = false
      
      if (this.arContent) {
        this.arContent.setAttribute('visible', 'false')
      }
      
      // Call the marker lost callback
      if (this.onMarkerLostCallback) {
        this.onMarkerLostCallback()
      }
    })

    console.log('AREngine: Event listeners set up for ROQ demo controller events')
  }

  /**
   * Register callback for marker found events.
   * The callback receives the targetIndex so the caller can resolve
   * the correct character for multi-target collections.
   */
  onMarkerFound(callback: (targetIndex: number) => void): void {
    this.onMarkerFoundCallback = callback
  }

  /**
   * Register callback for marker lost events
   */
  onMarkerLost(callback: () => void): void {
    this.onMarkerLostCallback = callback
  }

  /**
   * Register callback for animation complete events
   */
  onAnimationComplete(callback: () => void): void {
    this.onAnimationCompleteCallback = callback
  }



  /**
   * Save animation state before MindAR target loss
   * Manage CTA visibility during tracking interruptions
   * Requirements: 5.1, 5.2
   */
  private saveAnimationStateBeforeLoss(): void {
    // Save whether animation was completed before MindAR target loss
    this.wasAnimationCompleteBeforeLoss = (this.animationState === 'completed')
    
    // Save whether 3D CTAs were visible before MindAR target loss
    this.wereCTAsVisibleBeforeLoss = this.cta3D ? 
      this.cta3D.getAttribute('visible') === 'true' : false
    
    console.log(`Saving MindAR tracking state before target loss:`)
    console.log(`- Animation completed: ${this.wasAnimationCompleteBeforeLoss}`)
    console.log(`- 3D CTAs visible: ${this.wereCTAsVisibleBeforeLoss}`)
    console.log(`- Current animation state: ${this.animationState}`)
    
    // If animation is currently playing, pause it to preserve state
    if (this.animationState === 'playing') {
      console.log('Animation was playing - will be paused due to MindAR target loss')
    }
    
    // If CTAs were visible, they will be hidden and restored when target returns
    if (this.wereCTAsVisibleBeforeLoss) {
      console.log('3D CTAs were visible - will be restored when MindAR target returns')
    }
  }

  /**
   * Handle animation state when MindAR target is found again
   * Implements animation pause/resume functionality for MindAR tracking
   * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
   */
  private handleTargetFoundAnimationState(): void {
    console.log(`MindAR target found - restoring animation state`)
    console.log(`Previous state: completed=${this.wasAnimationCompleteBeforeLoss}, CTAs visible=${this.wereCTAsVisibleBeforeLoss}`)
    console.log(`Current animation state: ${this.animationState}`)
    
    // If animation was completed before MindAR target loss, restore CTAs immediately
    if (this.wasAnimationCompleteBeforeLoss && this.wereCTAsVisibleBeforeLoss) {
      console.log('Animation was complete before MindAR target loss - restoring CTAs')
      
      // Restore animation state to completed
      this.animationState = 'completed'
      
      // Show 3D CTAs again with MindAR tracking
      this.showCTA3D()
      
      // Notify application controller to restore 2D CTA and update state
      document.dispatchEvent(new CustomEvent('restoreCTAs', {
        detail: {
          source: 'mindar-tracking',
          animationWasComplete: true,
          ctasWereVisible: true
        }
      }))
      
    } else if (this.animationState === 'paused') {
      // If animation was paused due to MindAR target loss, resume it
      console.log('Animation was paused due to MindAR target loss - resuming with MindAR tracking')
      this.resumeAnimationFromMindARTracking()
      
    } else if (this.animationState === 'stopped' && this.isModelLoaded) {
      // If model is loaded but animation hasn't started, this might be a re-detection
      console.log('Model loaded but animation not started - checking if we should restart')
      // This case will be handled by the application controller calling playAnimation()
      
    } else {
      console.log('No specific animation state restoration needed')
    }
  }

  /**
   * Resume animation specifically when MindAR target returns to view
   * Requirements: 5.3, 5.4
   */
  private resumeAnimationFromMindARTracking(): void {
    if (!this.model || !this.videoElement) {
      console.warn('Cannot resume animation - no model or video loaded')
      return
    }

    try {
      if (this.animationState === 'paused') {
        console.log('Resuming ROQ video animation from MindAR tracking pause')
        this.animationState = 'playing'
        
        // Resume video and audio from where they were paused
        this.videoElement.muted = isIOS() ? !this.hasAudioUnlocked : false
        if (this.videoElement.paused) {
          this.videoElement.play().catch(error => {
            console.warn('Video resume from MindAR tracking failed:', error)
          })
        }
        this.playAudio()
        
        // Resume video container animations if needed (scaled down by half)
        const videoContainer = this.model.querySelector('#video-container')
        if (videoContainer && videoContainer.getAttribute('scale') !== '0.5 0.5 0.5') {
          videoContainer.setAttribute('animation__zoom_resume', 
            'property: scale; to: 0.5 0.5 0.5; dur: 1000; easing: easeOutBack')
        }
        
        // No completion timeout — video loops via the 'ended' event handler
        console.log('Video and audio resumed from paused position (MindAR re-track) — looping enabled')
      } else {
        console.log(`Cannot resume animation - current state: ${this.animationState}`)
      }
    } catch (error) {
      console.error('Failed to resume ROQ video animation from MindAR tracking:', error)
    }
  }

  /**
   * Load and position video content within marker.
   * Accepts an optional CharacterConfig for multi-target collections;
   * falls back to the single-client videoUrl from clientAssetConfig.
   * Requirements: 4.2, 4.3
   */
  async loadModel(character?: CharacterConfig): Promise<void> {
    if (!this.arContent) {
      throw new Error('AR content container not initialized')
    }

    // If a different character was detected while a model is already loaded,
    // tear down the old one so we can load the new character's video.
    if (this.isModelLoaded && this.model && character &&
        this.currentCharacter?.characterId !== character.characterId) {
      console.log(`Character changed (${this.currentCharacter?.characterId} → ${character.characterId}), reloading model`)
      this.model.remove()
      this.model = null
      if (this.videoElement) {
        this.videoElement.pause()
        this.videoElement.remove()
        this.videoElement = null
      }
      this.stopAudio()
      this.isModelLoaded = false
      this.animationState = 'stopped'
    }

    if (this.isModelLoaded && this.model) {
      console.log('Video content already loaded, skipping model creation')
      return
    }

    // Store which character we're loading
    if (character) {
      this.currentCharacter = character
    }

    // Pre-load and start background audio as soon as character is set,
    // so music plays throughout the page (not only when animation fires).
    const bgAudioUrl = this.currentCharacter?.audioUrl
    if (bgAudioUrl && !this.audioElement) {
      this.loadAudio(bgAudioUrl)
      // Attempt immediate play — will be silently blocked and unlocked on
      // first user gesture via setupAudioUnlockGesture if needed.
      this.playAudio()
    }

    try {
      console.log('Loading video content...', character?.name ?? '(single client)')

      // Create main content container positioned for MindAR tracking
      this.model = document.createElement('a-entity')
      this.model.setAttribute('id', 'roq-content')
      this.model.setAttribute('position', '0 0 0') // Positioned directly on the tracked image
      this.model.setAttribute('scale', '0.1 0.1 0.1') // Start small for entrance animation

      // Create video content instead of 3D logo
      this.createVideoContent()

      // Add content to AR scene
      this.arContent.appendChild(this.model)

      // Mark content as loaded
      this.isModelLoaded = true

      // Simulate content loading delay
      await new Promise(resolve => setTimeout(resolve, 500))

      console.log('Video content loaded successfully')

    } catch (error) {
      console.error('Failed to load video content:', error)
      throw new Error(`Model loading failed: ${error}`)
    }
  }

  /**
   * Create video content structure
   * Requirements: 4.2, 4.3
   */
  private createVideoContent(): void {
    if (!this.model) return

    // Create video container
    const videoContainer = document.createElement('a-entity')
    videoContainer.setAttribute('id', 'video-container')
    // Start slightly below the marker so we can "pull up" the animation above the pin
    videoContainer.setAttribute('position', '0 -0.6 0')
    videoContainer.setAttribute('scale', '0.01 0.01 0.01') // Start very small for entrance animation
    this.model.appendChild(videoContainer)

    // Create the video element and plane
    this.createVideoPlane(videoContainer)
  }


  /**
   * Create video plane for AR display
   * Requirements: 4.2, 4.3
   */
  private createVideoPlane(container: HTMLElement): void {
    // Create video element first
    const video = document.createElement('video')
    video.setAttribute('id', 'roq-video')
    video.setAttribute('crossorigin', 'anonymous')
    // Loop removed - video will play once and stop
    // Audio enabled - muted removed to allow audio playback
    video.setAttribute('playsinline', 'true') // Required for iOS
    video.setAttribute('webkit-playsinline', 'true')
    video.setAttribute('preload', 'auto')
    video.style.display = 'none' // Hide the actual video element
    if (isIOSSafari()) {
      // Start muted on iOS Safari for reliable autoplay, then unlock on user tap.
      video.muted = true
    }
    // NOTE: We intentionally do NOT call setupAudioBoost(video) here.
    // createMediaElementSource() routes audio through the WebAudio API, which
    // requires an AudioContext that was resumed inside a user gesture.  If the
    // context is suspended (e.g. created during marker detection, not a click)
    // the entire video audio is silenced.  Letting HTML5 handle video audio
    // natively is more reliable across browsers.
    
    // Add error handling for video loading
    video.addEventListener('error', (e) => {
      console.error('Video loading error:', e)
      console.warn('Video format may not be supported by this browser')
    })
    
    video.addEventListener('loadedmetadata', () => {
      console.log(`Video loaded: ${video.videoWidth}x${video.videoHeight}, duration: ${video.duration}s`)
    })
    
    // When video ends: loop back to start and keep playing
    video.addEventListener('ended', () => {
      if (this.animationState === 'playing') {
        console.log('Video ended — looping from start')
        this.onAnimationCompleteCallback?.()
        video.currentTime = 0
        video.play().catch(err => console.warn('Video loop restart failed:', err))
        // Background audio already loops (audio.loop = true in loadAudio)
      }
    })
    
    // Use character-specific video URL for multi-target collections,
    // or fall back to the single-client videoUrl from asset config.
    // On iOS Chrome, prefer chromeIOSVideoUrl (.mp4) to avoid black-box rendering with WebM.
    // On iOS Safari, prefer iosVideoUrl (.mov) over .webm which is unsupported.
    let videoUrl: string | undefined
    if (isIOSChrome() && this.currentCharacter?.chromeIOSVideoUrl) {
      videoUrl = this.currentCharacter.chromeIOSVideoUrl
    } else if (isIOSSafari() && this.currentCharacter?.iosVideoUrl) {
      videoUrl = this.currentCharacter.iosVideoUrl
    } else {
      videoUrl = this.currentCharacter?.videoUrl || this.clientAssetConfig.videoUrl
    }
    if (videoUrl) {
      video.setAttribute('src', videoUrl)
    } else {
      console.warn('No video URL available for this character — audio-only experience')
    }
    
    // Add video to document body (required for A-Frame video textures)
    document.body.appendChild(video)

    // Keep a reference to the video element immediately so other
    // methods (playAnimation/resumeAnimation) can control playback
    // even if the A-Frame plane hasn't been created yet.
    this.videoElement = video

    
    // Create the video plane only after the browser can play the video.
    // This prevents WebGL errors like "texImage2D: no video" when the
    // texture is uploaded before frames are available.
    const createPlane = () => {
      try {
        const videoPlane = document.createElement('a-plane')

        // Compute plane size based on video aspect so greenscreen subjects are not squashed
        const aspect = (video.videoWidth && video.videoHeight) ? (video.videoWidth / video.videoHeight) : (16 / 9)
        const baseWidth = 4
        const computedHeight = baseWidth / aspect
        videoPlane.setAttribute('width', String(baseWidth))
        videoPlane.setAttribute('height', String(computedHeight))

        // Prefer the chroma-key shader whenever it's registered. Fall back to flat
        // shader if chroma-key is not available for some reason.
        const AFRAME = (window as any).AFRAME
        let materialAttr: string
        const isHarrietTubman = this.currentCharacter?.characterId === 'harriet'
        // Use chroma-key only on iOS where luma-keying is required for .mov transparency
        if ((isIOSSafari() || isIOSChrome()) && AFRAME && AFRAME.shaders && AFRAME.shaders['chroma-key-v2']) {
          if (isHarrietTubman && AFRAME.shaders['chroma-key-magenta-v1']) {
            materialAttr = `shader: chroma-key-magenta-v1; src: #roq-video; transparent: true; similarity: 0.36; smoothness: 0.06; spillStrength: 0.8`
          } else {
            materialAttr = `shader: chroma-key-v2; src: #roq-video; transparent: true; similarity: 0.40; smoothness: 0.05; spillStrength: 0.9`
          }
        } else {
          materialAttr = `shader: flat; src: #roq-video; transparent: true; alphaTest: 0.1`
        }
        
        videoPlane.setAttribute('material', materialAttr)
        videoPlane.setAttribute('position', '0 0 0')
        container.appendChild(videoPlane)

        // Debug: log material and video state to help diagnose invisible plane issues
        try {
          console.log('AREngine: videoPlane created', {
            materialAttr,
            aspect,
            videoId: video.id,
            videoReadyState: video.readyState,
            videoSrc: video.getAttribute('src')
          })
        } catch (e) {
          console.warn('AREngine: failed to log videoPlane debug info', e)
        }

        // Additional A-Frame object3D debug (delayed so A-Frame has time to attach object3D)
        setTimeout(() => {
          try {
            const planeObj = (videoPlane as any).object3D
            const modelObj = (this.model as any)?.object3D
            console.log('AREngine: object3D debug', {
              planeVisible: planeObj ? planeObj.visible : undefined,
              planeScale: planeObj ? planeObj.scale : undefined,
              planePosition: planeObj ? planeObj.position : undefined,
              modelVisible: modelObj ? modelObj.visible : undefined,
              modelScale: modelObj ? modelObj.scale : undefined,
              modelPosition: modelObj ? modelObj.position : undefined
            })
          } catch (e) {
            console.warn('AREngine: object3D debug failed', e)
          }
        }, 200)
        // Store reference to video element for control
        this.videoElement = video
        console.log('AREngine: Video plane created after canplay event')
      } catch (err) {
        console.error('Failed to create video plane after canplay:', err)
      }
    }

    // If the video is already ready, create immediately; otherwise wait for canplay
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      createPlane()
    } else {
      const onCanPlay = () => {
        video.removeEventListener('canplay', onCanPlay)
        createPlane()
      }
      video.addEventListener('canplay', onCanPlay)
    }
  }




  /**
   * Play video animation with entrance effects
   * Requirements: 4.3, 4.4
   */
  playAnimation(): void {
    if (!this.model || !this.videoElement) {
      console.warn('No model or video loaded for animation')
      return
    }

    if (this.animationState === 'playing' || this.animationState === 'completed') {
      console.log('Animation already playing or completed')
      return
    }

    try {
      console.log('Playing ROQ video animation...')
      this.animationState = 'playing'

      // Clear any existing timeout
      if (this.animationTimeout) {
        clearTimeout(this.animationTimeout)
      }

      // Add and show 3D CTA at the top when animation starts
      if (!this.cta3D) {
        this.addCTA3D()
      }
      this.showCTA3D()

      // Load background audio for this character if available
      const audioUrl = this.currentCharacter?.audioUrl
      if (audioUrl && !this.audioElement) {
        this.loadAudio(audioUrl)
      }

      // Start video playback.
      if (this.videoElement && this.videoElement.src) {
        this.videoElement.muted = isIOS() ? !this.hasAudioUnlocked : false
        this.videoElement.volume = 1.0
        this.videoElement.play().then(() => {
          console.log('Video started playing')
          this.playAudio()
          if (isIOS() && !this.hasAudioUnlocked) {
            this.setupAudioUnlockGesture()
          }
        }).catch(error => {
          console.error('Video autoplay failed:', error)
          this.playAudio()
          if (isIOS()) {
            this.setupAudioUnlockGesture()
          }
        })
      } else if (audioUrl) {
        // Audio-only experience (e.g. Spindiana — no video)
        this.playAudio()
      }

      // Start with overall model scale animation (scaled down by half)
      this.model.setAttribute('animation__entrance', 'property: scale; to: 0.6 0.6 0.6; dur: 1000; easing: easeOutQuart')
      
      // Get video container and animate zoom-in from center
      const videoContainer = this.model.querySelector('#video-container')
      if (videoContainer) {
        // Delay video zoom to start after entrance
        setTimeout(() => {
          videoContainer.setAttribute('animation__zoom', 
            'property: scale; to: 0.6 0.6 0.6; dur: 1500; easing: easeOutBack')
          // Pull the video up above the marker (gives a "lift" or "pop up" effect)
          try {
            videoContainer.setAttribute('animation__pullup', 'property: position; to: 0 0 0; dur: 800; easing: easeOutBack')
          } catch (e) {
            console.warn('Failed to set pull-up animation on video container:', e)
          }
        }, 500)
      }

      // No completion timeout — video loops via the 'ended' event handler above

    } catch (error) {
      console.error('Failed to play video animation:', error)
      this.animationState = 'stopped'
    }
  }


  /**
   * Pause video animation when MindAR target is lost
   * Implements animation pause on MindAR target lost
   * Requirements: 5.1, 5.2
   */
  pauseAnimation(): void {
    if (!this.model || !this.videoElement) {
      console.warn('Cannot pause animation - no model or video loaded')
      return
    }

    try {
      if (this.animationState === 'playing') {
        console.log('Pausing ROQ video animation due to MindAR target loss')
        this.animationState = 'paused'
        
        // Clear the completion timeout to preserve animation state
        if (this.animationTimeout) {
          clearTimeout(this.animationTimeout)
          this.animationTimeout = null
          console.log('Animation completion timeout cleared for MindAR pause')
        }
        
        // Pause video when target is lost; keep background audio running continuously
        this.videoElement.pause()
        if (!this.currentCharacter?.audioUrl) {
          this.pauseAudio()
        }
        console.log('Video paused — will resume from current position when target returns')
        
        // Pause video container zoom animation
        const videoContainer = this.model.querySelector('#video-container')
        if (videoContainer) {
          videoContainer.removeAttribute('animation__zoom')
          videoContainer.removeAttribute('animation__zoom_resume')
        }
        
        // Pause main model animations
        this.model.removeAttribute('animation__entrance')
        
        console.log('ROQ video animation successfully paused for MindAR tracking - can be resumed when target returns')
      } else if (this.animationState === 'completed') {
        console.log('Animation was already completed - no pause needed, but saving state for MindAR tracking')
        // Animation is complete, just ensure state is preserved for restoration
      } else {
        console.log(`Animation pause requested but current state is: ${this.animationState}`)
      }
    } catch (error) {
      console.error('Failed to pause ROQ video animation for MindAR tracking:', error)
    }
  }

  /**
   * Resume video animation from paused state when MindAR target returns
   * Add animation resume functionality when ROQ logo returns to view
   * Requirements: 5.3, 5.4
   */
  resumeAnimation(): void {
    if (!this.model || !this.videoElement) {
      console.warn('Cannot resume animation - no model or video loaded')
      return
    }

    try {
      if (this.animationState === 'paused') {
        console.log('Resuming ROQ video animation from paused state due to MindAR target return')
        this.animationState = 'playing'
        
        // Resume video playback with iOS audio state preserved.
        this.videoElement.muted = isIOS() ? !this.hasAudioUnlocked : false
        if (this.videoElement.paused) {
          this.videoElement.play().catch(error => {
            console.warn('Video resume failed:', error)
          })
        } else {
          console.log('Video already playing - continuing playback')
        }
        
        // Resume video container animations if needed (scaled down by half)
        const videoContainer = this.model.querySelector('#video-container')
        if (videoContainer && videoContainer.getAttribute('scale') !== '0.6 0.6 0.6') {
          videoContainer.setAttribute('animation__zoom_resume', 
            'property: scale; to: 0.6 0.6 0.6; dur: 1000; easing: easeOutBack')
        }
        
        // No completion timeout — video loops via the 'ended' event handler
        console.log('Video and audio resumed from paused position — looping enabled')
      } else if (this.animationState === 'completed') {
        console.log('Animation was already completed - no resume needed')
      } else {
        console.log(`Cannot resume animation - current state: ${this.animationState}`)
      }
    } catch (error) {
      console.error('Failed to resume ROQ video animation from MindAR tracking:', error)
    }
  }

  /**
   * Add 3D CTA element above the marker
   * Requirements: 6.1, 6.2
   */
  addCTA3D(): void {
    if (!this.arContent) {
      console.warn('AR content container not available for CTA')
      return
    }

    // Check if CTA is already created to prevent duplicates
    if (this.cta3D) {
      console.log('3D CTA already exists, skipping creation')
      return
    }

  //   try {
  //     console.log('Adding 3D CTA element...')

  //     // Create 3D CTA container positioned at the very top above "THE PRINTVERSE"
  //     this.cta3D = document.createElement('a-entity')
  //     this.cta3D.setAttribute('id', 'cta-3d')
  //     this.cta3D.setAttribute('position', '0 3.2 0') // Positioned above the video (video is 6 units tall, top at Y=3, scaled to 0.5 makes visual top at Y=1.5)
  //     this.cta3D.setAttribute('visible', 'false') // Will be made visible by showCTA3D()
  //     this.cta3D.setAttribute('class', 'clickable')

  //     // Create main CTA button with planet-style colors
  //     const ctaButton = document.createElement('a-box')
  //     ctaButton.setAttribute('width', '1.2') // Increased width for better visibility
  //     ctaButton.setAttribute('height', '0.4') // Increased height for better visibility
  //     ctaButton.setAttribute('depth', '0.1')
  //     ctaButton.setAttribute('color', '#80797A') // Grey color matching planets
  //     ctaButton.setAttribute('metalness', '0.4')
  //     ctaButton.setAttribute('roughness', '0.6')
  //     ctaButton.setAttribute('position', '0 0 0')
  //     ctaButton.setAttribute('class', 'clickable')
      
  //     // No hover effects - keep static grey color
      
  //     this.cta3D.appendChild(ctaButton)

  //     // Add main CTA text with lime color
  //     const ctaText = document.createElement('a-text')
  //     const ctaLabel = this.currentCharacter?.ctaText ||
  //       this.clientAssetConfig.characters?.[0]?.ctaText || 'Learn More'
  //     ctaText.setAttribute('value', ctaLabel)
  //     ctaText.setAttribute('position', '0 0.02 0.06')
  //     ctaText.setAttribute('align', 'center')
  //     ctaText.setAttribute('color', '#C8E66A') // Lighter lime color
  //     ctaText.setAttribute('scale', '0.8 0.8 0.8') // Increased size for better visibility
  //     ctaText.setAttribute('font', 'roboto')
  //     ctaText.setAttribute('class', 'clickable')
  //     this.cta3D.appendChild(ctaText)

  //     // Add subtitle text with grey color
  //     const subtitleText = document.createElement('a-text')
  //     subtitleText.setAttribute('value', 'Tap to Learn More')
  //     subtitleText.setAttribute('position', '0 -0.08 0.06')
  //     subtitleText.setAttribute('align', 'center')
  //     subtitleText.setAttribute('color', '#9B9495') // Light grey color
  //     subtitleText.setAttribute('scale', '0.4 0.4 0.4') // Increased size for better visibility
  //     subtitleText.setAttribute('font', 'roboto')
  //     subtitleText.setAttribute('class', 'clickable')
  //     this.cta3D.appendChild(subtitleText)

  //     // Add simple arrow pointing down to the logo
  //     const arrow = document.createElement('a-cone')
  //     arrow.setAttribute('color', '#C8E66A') // Lighter lime color
  //     arrow.setAttribute('radius-bottom', '0.04')
  //     arrow.setAttribute('radius-top', '0')
  //     arrow.setAttribute('height', '0.1')
  //     arrow.setAttribute('position', '0 -0.25 0')
  //     arrow.setAttribute('rotation', '180 0 0')
  //     arrow.setAttribute('metalness', '0.4')
  //     arrow.setAttribute('roughness', '0.6')
      
  //     // Simple bounce animation without glow
  //     arrow.setAttribute('animation__bounce', 'property: position; to: 0 -0.3 0; dir: alternate; loop: true; dur: 1500; easing: easeInOutSine')
      
  //     this.cta3D.appendChild(arrow)

  //     // Set up click event using A-Frame raycaster
  //     ctaButton.addEventListener('click', (event: Event) => {
  //       event.preventDefault()
  //       event.stopPropagation()
  //       console.log('3D CTA clicked')
  //       this.handleCTA3DClick()
  //     })

  //     // No hover effects - keep static appearance

  //     // Add CTA to AR content
  //     this.arContent.appendChild(this.cta3D)

  //     console.log('3D CTA element added successfully')

  //   } catch (error) {
  //     console.error('Failed to add 3D CTA:', error)
  //   }
   }



  /**
   * Handle 3D CTA click with visual feedback
   * Requirements: 6.4
   */
  private handleCTA3DClick(): void {
    // Add click animation
    if (this.cta3D) {
      this.cta3D.setAttribute('animation__click', 'property: scale; to: 0.9 0.9 0.9; dur: 100; dir: alternate; loop: 1')
    }

    // Dispatch custom event for application controller
    document.dispatchEvent(new CustomEvent('ctaClick', { 
      detail: { type: '3D' } 
    }))
  }

  /**
   * Show 3D CTA element with entrance animation for MindAR tracking
   * Manage CTA visibility during tracking interruptions
   * Requirements: 5.4, 5.5, 6.2
   */
  showCTA3D(): void {
    if (this.cta3D) {
      console.log('Showing 3D CTA with MindAR tracking entrance animation')
      console.log('CTA element:', this.cta3D)
      console.log('CTA parent:', this.cta3D.parentElement)
      console.log('AR Content visible:', this.arContent?.getAttribute('visible'))
      
      // Clear any existing exit animations from previous MindAR target loss
      this.cta3D.removeAttribute('animation__exit')
      this.cta3D.removeAttribute('animation__float_out')
      
      // Reset position and make visible - positioned at the very top above "THE PRINTVERSE"
      this.cta3D.setAttribute('position', '0 3.2 0')
      this.cta3D.setAttribute('visible', 'true')
      this.cta3D.setAttribute('scale', '1.2 1.2 1.2') // Slightly larger scale for better visibility
      
      // Animate entrance with MindAR-optimized timing (fade in effect)
      this.cta3D.setAttribute('animation__entrance', 'property: scale; to: 1.2 1.2 1.2; dur: 500; easing: easeOutBack')
      this.cta3D.setAttribute('animation__float_in', 'property: position; to: 0 3.2 0; dur: 500; easing: easeOutQuart')
      
      console.log('3D CTA shown with MindAR tracking animation - position:', this.cta3D.getAttribute('position'), 'visible:', this.cta3D.getAttribute('visible'), 'scale:', this.cta3D.getAttribute('scale'))
    } else {
      console.warn('Cannot show 3D CTA - CTA element not created yet')
    }
  }

  /**
   * Hide 3D CTA element with exit animation during MindAR tracking interruptions
   * Manage CTA visibility during tracking interruptions
   * Requirements: 5.1, 5.2
   */
  hideCTA3D(): void {
    if (this.cta3D) {
      console.log('Hiding 3D CTA due to MindAR target loss')
      
      // Clear any existing entrance animations from MindAR target detection
      this.cta3D.removeAttribute('animation__entrance')
      this.cta3D.removeAttribute('animation__float_in')
      
      // Animate exit with MindAR-optimized timing (faster for tracking loss)
      this.cta3D.setAttribute('animation__exit', 'property: scale; to: 0.1 0.1 0.1; dur: 300; easing: easeInQuart')
      this.cta3D.setAttribute('animation__float_out', 'property: position; to: 0 3.5 0; dur: 300; easing: easeInQuart')
      
      // Hide after animation completes (shorter delay for MindAR tracking)
      setTimeout(() => {
        if (this.cta3D) {
          this.cta3D.setAttribute('visible', 'false')
          console.log('3D CTA hidden after MindAR target loss animation')
        }
      }, 300)
      
      console.log('3D CTA hiding animation started for MindAR target loss')
    } else {
      console.log('No 3D CTA to hide')
    }
  }

  private showTapForSoundPrompt(): void {
    if (this.soundPromptElement) return

    const prompt = document.createElement('button')
    prompt.type = 'button'
    prompt.className = 'tap-for-sound-prompt'
    prompt.setAttribute('aria-label', 'Tap for sound')
    prompt.innerHTML = '<span class="sound-icon" aria-hidden="true">🔊</span><span>Tap for Sound</span>'
    document.body.appendChild(prompt)
    this.soundPromptElement = prompt
  }

  private setupAudioBoost(video: HTMLVideoElement): void {
    try {
      if (this.audioContext && this.boostedAudioSource && this.boostedGainNode) {
        return
      }

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
      if (!AudioContextClass) return

      this.audioContext = new AudioContextClass()
      this.boostedAudioSource = this.audioContext.createMediaElementSource(video)
      this.boostedGainNode = this.audioContext.createGain()
      this.boostedGainNode.gain.value = 1.7
      this.boostedAudioSource.connect(this.boostedGainNode)
      this.boostedGainNode.connect(this.audioContext.destination)
    } catch (error) {
      console.warn('Audio boost setup skipped:', error)
    }
  }

  private hideTapForSoundPrompt(): void {
    if (this.soundPromptElement) {
      this.soundPromptElement.classList.add('tap-for-sound-prompt--hiding')
      setTimeout(() => {
        this.soundPromptElement?.remove()
        this.soundPromptElement = null
      }, 300)
    }
  }

  // ---------------------------------------------------------------------------
  // Background audio (separate mp3 per character)
  // ---------------------------------------------------------------------------

  private loadAudio(audioUrl: string): void {
    if (this.audioElement) {
      this.audioElement.pause()
      this.audioElement.src = ''
      this.audioElement.remove()
      this.audioElement = null
    }
    const audio = document.createElement('audio')
    audio.src = audioUrl
    audio.loop = true
    audio.volume = 0.3
    audio.preload = 'auto'
    if (isIOS()) {
      audio.muted = true // will be unmuted on first user gesture
    }
    document.body.appendChild(audio)
    this.audioElement = audio
  }

  private playAudio(): void {
    if (!this.audioElement) return
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {})
    }
    this.audioElement.muted = isIOS() ? !this.hasAudioUnlocked : false
    this.audioElement.play().catch(err => {
      console.warn('Background audio autoplay blocked:', err)
    })
  }

  private pauseAudio(): void {
    if (this.audioElement && !this.audioElement.paused) {
      this.audioElement.pause()
    }
  }

  stopAudio(): void {
    if (this.audioElement) {
      this.audioElement.pause()
      this.audioElement.currentTime = 0
      this.audioElement.remove()
      this.audioElement = null
    }
  }

  pauseAudioOnly(): void {
    if (this.audioElement && !this.audioElement.paused) {
      this.audioElement.pause()
    }
    if (this.videoElement && !this.videoElement.paused) {
      this.videoElement.pause()
    }
  }

  private setupAudioUnlockGesture(): void {
    if (this.pendingAudioUnlockHandler || !this.videoElement) return

    this.showTapForSoundPrompt()

    this.pendingAudioUnlockHandler = async () => {
      try {
        if (this.audioContext && this.audioContext.state === 'suspended') {
          await this.audioContext.resume()
        }
        if (this.videoElement) {
          this.videoElement.muted = false
          this.videoElement.volume = 1.0
          await this.videoElement.play()
        }
        // Also unmute background audio element
        if (this.audioElement) {
          this.audioElement.muted = false
          await this.audioElement.play().catch(() => {})
        }
        this.hasAudioUnlocked = true
        this.hideTapForSoundPrompt()
        this.removeAudioUnlockGesture()
      } catch (error) {
        console.warn('Audio unlock tap received but playback still blocked:', error)
      }
    }

    document.addEventListener('touchstart', this.pendingAudioUnlockHandler, { passive: true, capture: true })
    document.addEventListener('click', this.pendingAudioUnlockHandler, { capture: true })
    this.soundPromptElement?.addEventListener('click', this.pendingAudioUnlockHandler)
  }

  private removeAudioUnlockGesture(): void {
    if (!this.pendingAudioUnlockHandler) return
    document.removeEventListener('touchstart', this.pendingAudioUnlockHandler, { capture: true })
    document.removeEventListener('click', this.pendingAudioUnlockHandler, { capture: true })
    this.soundPromptElement?.removeEventListener('click', this.pendingAudioUnlockHandler)
    this.pendingAudioUnlockHandler = null
  }

  /**
   * Get the A-Frame scene element
   */
  getScene(): HTMLElement | null {
    return this.scene
  }

  /**
   * Check if AR engine is initialized
   */
  isReady(): boolean {
    return this.isInitialized
  }

  /**
   * Get current animation state
   * Requirements: 5.1, 5.2, 5.3, 5.4
   */
  getAnimationState(): 'stopped' | 'playing' | 'paused' | 'completed' {
    return this.animationState
  }

  /**
   * Reset and replay animation
   * Resets video to beginning and allows animation to play again
   */
  resetAnimation(): void {
    console.log('Resetting animation for replay...')
    
    // Clear any existing timeout
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout)
      this.animationTimeout = null
    }
    this.removeAudioUnlockGesture()
    this.hideTapForSoundPrompt()
    if (this.audioContext) {
      this.audioContext.close().catch(() => {})
      this.audioContext = null
      this.boostedAudioSource = null
      this.boostedGainNode = null
    }
    
    // Reset animation state
    this.animationState = 'stopped'

    // Stop background audio
    this.stopAudio()
    this.currentCharacter = null

    // Reset video to beginning
    if (this.videoElement) {
      this.videoElement.currentTime = 0
      this.videoElement.pause()
      console.log('Video reset to beginning')
    }
    
    // Reset model scale to initial state
    if (this.model) {
      this.model.setAttribute('scale', '0.1 0.1 0.1')
      const videoContainer = this.model.querySelector('#video-container')
      if (videoContainer) {
        videoContainer.setAttribute('scale', '0.01 0.01 0.01')
      }
    }
    
    // Hide 3D CTA if visible
    if (this.cta3D) {
      this.cta3D.setAttribute('visible', 'false')
    }
    
    // Reset state tracking
    this.wasAnimationCompleteBeforeLoss = false
    this.wereCTAsVisibleBeforeLoss = false
    this.hasAudioUnlocked = false
    
    console.log('Animation reset complete - ready to replay')
  }

  /**
   * Update animation state management for MindAR events
   * Provides centralized state management for MindAR tracking events
   * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
   */
  updateAnimationStateForMindAR(event: 'target-found' | 'target-lost'): void {
    console.log(`Updating animation state for MindAR event: ${event}`)
    console.log(`Current state before update: ${this.animationState}`)
    
    if (event === 'target-found') {
      // Handle animation state when MindAR target is found
      this.handleTargetFoundAnimationState()
      
    } else if (event === 'target-lost') {
      // Handle animation state when MindAR target is lost
      this.saveAnimationStateBeforeLoss()
      
      // Pause animation if it's currently playing
      if (this.animationState === 'playing') {
        this.pauseAnimation()
      }
      
      // Hide CTAs during tracking interruption
      this.hideCTA3D()
    }
    
    console.log(`Animation state after MindAR ${event}: ${this.animationState}`)
  }

  /**
   * Get MindAR tracking state information
   * Useful for debugging and state management
   * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
   */
  getMindARTrackingState(): {
    isTargetDetected: boolean;
    animationState: string;
    wasCompleteBeforeLoss: boolean;
    wereCTAsVisibleBeforeLoss: boolean;
    isModelLoaded: boolean;
  } {
    return {
      isTargetDetected: this.isTargetDetected,
      animationState: this.animationState,
      wasCompleteBeforeLoss: this.wasAnimationCompleteBeforeLoss,
      wereCTAsVisibleBeforeLoss: this.wereCTAsVisibleBeforeLoss,
      isModelLoaded: this.isModelLoaded
    }
  }

  /**
   * Set up recording canvas for video capture
   * Phase 2: Recording feature
   * Note: Recording canvas is now managed by VideoRecorder
   */
  private setupRecordingCanvas(): void {
    // Recording canvas is now created and managed by VideoRecorder
    // This method kept for compatibility but no longer needed
    console.log('Recording setup - VideoRecorder will handle canvas creation')
  }

  /**
   * Start recording AR experience with camera feed
   * Phase 2: Recording feature - records camera + AR overlay
   */
  async startRecording(): Promise<boolean> {
    if (!this.scene) {
      console.warn('Cannot start recording - scene not initialized')
      return false
    }

    // Get A-Frame canvas (AR overlay)
    const aframeCanvas = this.scene.querySelector('canvas') as HTMLCanvasElement
    if (!aframeCanvas) {
      console.warn('Cannot start recording - A-Frame canvas not found')
      return false
    }

    // Get video element - it might not be created yet, so try to get it from DOM
    if (!this.videoElement) {
      // Try to get video element from DOM by ID (it might have been created but not stored)
      const videoFromDOM = document.getElementById('roq-video') as HTMLVideoElement
      if (videoFromDOM) {
        console.log('AREngine: Found video element in DOM, using it for recording')
        this.videoElement = videoFromDOM
      } else {
        console.warn('AREngine: Video element not found - it may not be created yet')
        console.warn('AREngine: Video will be captured when it starts playing')
      }
    }
    
    // Ensure ROQ video is playing with audio enabled for recording
    if (this.videoElement) {
      // Ensure video is not muted FIRST
      this.videoElement.muted = false
      this.videoElement.volume = 1.0
      console.log('AREngine: Ensuring ROQ video is unmuted and volume is max for recording')
      
      // If video is paused, start playing it
      if (this.videoElement.paused) {
        console.log('AREngine: Starting ROQ video playback for recording')
        try {
          await this.videoElement.play()
          
          // Wait a moment for video to actually start playing and produce audio
          await new Promise(resolve => setTimeout(resolve, 300))
          
          console.log('AREngine: ROQ video started playing for recording')
          console.log('AREngine: Video state:', {
            paused: this.videoElement.paused,
            muted: this.videoElement.muted,
            volume: this.videoElement.volume,
            readyState: this.videoElement.readyState,
            currentTime: this.videoElement.currentTime
          })
        } catch (error) {
          console.warn('AREngine: Failed to start video playback for recording:', error)
        }
      } else {
        console.log('AREngine: ROQ video is already playing')
        // Still ensure it's unmuted and volume is up
        this.videoElement.muted = false
        this.videoElement.volume = 1.0
      }
      
      // Double-check audio is enabled
      if (this.videoElement.muted) {
        console.warn('AREngine: Video is still muted after unmute attempt!')
        this.videoElement.muted = false
      }
    } else {
      console.warn('AREngine: No video element available for audio capture')
      console.warn('AREngine: Recording will continue with microphone audio only')
    }

    // Start recording with camera + AR canvas
    // VideoRecorder will handle getting camera access and compositing
    // Audio enabled to capture both microphone and AR video audio
    console.log('AREngine: Passing video element to recorder:', {
      hasVideoElement: !!this.videoElement,
      videoId: this.videoElement?.id,
      videoSrc: this.videoElement?.src,
      videoPaused: this.videoElement?.paused,
      videoMuted: this.videoElement?.muted
    })
    
    const success = await this.videoRecorder.startRecording(aframeCanvas, {
      includeAudio: true, // Enable audio recording
      includeMicrophone: false, // Keep only animation audio (no ambient mic background noise)
      arVideoElement: this.videoElement || undefined, // Pass AR video element to capture its audio
      videoBitsPerSecond: 5000000, // 5 Mbps for high quality
      audioBitsPerSecond: 192000 // slightly higher bitrate for clearer animation audio
    })
    
    if (success) {
      console.log('AR recording started with camera feed and audio')
    } else {
      console.error('Failed to start recording')
    }

    return success
  }

  /**
   * Stop recording AR experience
   * Phase 2: Recording feature
   */
  async stopRecording(): Promise<any> {
    console.log('AREngine: Stopping recording...')
    
    // Stop the ROQ video playback when recording stops
    if (this.videoElement) {
      console.log('AREngine: Pausing ROQ video playback')
      this.videoElement.pause()
      this.animationState = 'paused'
    }
    
    const result = await this.videoRecorder.stopRecording()
    
    console.log('AREngine: Recording stopped, result:', result)
    console.log('AREngine: Result details:', {
      success: result?.success,
      hasUrl: !!result?.url,
      hasBlob: !!result?.blob,
      format: result?.format,
      error: result?.error,
      type: result?.type
    })
    
    // Always dispatch event so we can handle both success and error cases
    if (result && (result.success || result.error)) {
      console.log('AREngine: Dispatching recordingComplete event')
      
      // Dispatch event with recording result (even if failed, so UI can show error)
      document.dispatchEvent(new CustomEvent('recordingComplete', {
        detail: result
      }))
      
      console.log('AREngine: recordingComplete event dispatched')
    } else {
      console.error('AREngine: Invalid result structure - cannot dispatch event:', result)
    }

    return result
  }

  /**
   * Check if currently recording
   */
  isRecording(): boolean {
    return this.videoRecorder.getIsRecording()
  }

  /**
   * Cleanup AR scene and resources
   */
  destroy(): void {
    // Stop recording if active (Phase 2)
    if (this.videoRecorder.getIsRecording()) {
      this.videoRecorder.stopRecording()
    }
    this.videoRecorder.destroy()

    // Clear animation timeout
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout)
      this.animationTimeout = null
    }
    
    // Clean up content video element (AR experience video)
    if (this.videoElement) {
      this.videoElement.pause()
      if (this.videoElement.parentNode) {
        this.videoElement.parentNode.removeChild(this.videoElement)
      }
      this.videoElement = null
    }

    // Clean up background audio
    this.stopAudio()
    this.currentCharacter = null

    // Recording canvas cleanup is handled by VideoRecorder

    // Remove the a-scene FIRST so A-Frame/MindAR's own disconnectedCallback
    // lifecycle runs and stops the camera stream cleanly.  If we stop the
    // stream ourselves before this, MindAR's internal stop() call tries to
    // call getTracks() on a null stream and throws.
    if (this.scene && this.scene.parentNode) {
      this.scene.parentNode.removeChild(this.scene)
    }

    // After the scene is gone, sweep up any camera <video> elements that
    // MindAR left in the DOM.  MindAR already stopped the tracks above so
    // we only need to remove the element — no getTracks() call needed.
    // Camera stream videos have no src attribute (they use srcObject), so
    // filtering on !v.src reliably targets only MindAR camera elements.
    document.querySelectorAll<HTMLVideoElement>('video').forEach(v => {
      if (v.id === 'roq-video') return // skip content videos
      if (!v.src || v.src === '' || v.src === window.location.href) {
        v.parentNode?.removeChild(v)
      }
    })

    // Remove MindAR-injected scanning-bar UI elements that live outside the
    // a-scene element (appended directly to the container or body).  Without
    // this cleanup a new scanning bar is added every time initializeScene() is
    // called, causing multiple animated bars to stack on top of each other.
    const mindArRoots: (HTMLElement | Document)[] = []
    if (this.container) mindArRoots.push(this.container)
    mindArRoots.push(document.body)
    mindArRoots.forEach(root => {
      root.querySelectorAll<HTMLElement>(
        '[class*="mindar-ui"], [id*="mindar"], [class*="mindar-image"]'
      ).forEach(el => el.parentNode?.removeChild(el))
    })

    this.container = null
    this.scene = null
    this.arContent = null
    this.imageTargets = []
    this.arContents = []
    this.model = null
    this.cta3D = null
    this.isInitialized = false
    this.isModelLoaded = false
    this.animationState = 'stopped'
    this.isTargetDetected = false
    this.wasAnimationCompleteBeforeLoss = false
    this.wereCTAsVisibleBeforeLoss = false
    
    console.log('AR engine destroyed')
  }



  /**
   * Registers a custom A-Frame luma-key shader if not already registered.
   * Used on iOS Safari where .mov video textures don't expose an alpha channel
   * through WebGL — the shader discards near-black pixels in GLSL instead.
  */
  // private registerLumaKeyShader(): void {
  //   const AFRAME = (window as any).AFRAME
  //   if (!AFRAME || AFRAME.shaders['luma-key']) return
  //   AFRAME.registerShader('luma-key', {
  //     schema: {
  //       src:       { type: 'map',    is: 'uniform' },
  //       threshold: { type: 'number', is: 'uniform', default: 0.05 },
  //       smoothing: { type: 'number', is: 'uniform', default: 0.05 }
  //     },
  //     vertexShader: [
  //       'varying vec2 vUv;',
  //       'void main() {',
  //       '  vUv = uv;',
  //       '  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);',
  //       '}'
  //     ].join('\n'),
  //     fragmentShader: [
  //       'uniform sampler2D src;',
  //       'uniform float threshold;',
  //       'uniform float smoothing;',
  //       'varying vec2 vUv;',
  //       'void main() {',
  //       '  vec4 color = texture2D(src, vUv);',
  //       '  float luma = dot(color.rgb, vec3(0.299, 0.587, 0.114));',
  //       '  float alpha = smoothstep(threshold - smoothing, threshold + smoothing, luma);',
  //       '  gl_FragColor = vec4(color.rgb, alpha);',
  //       '}'
  //     ].join('\n')
  //   })
  //   console.log('AREngine: luma-key shader registered for iOS Safari')
  // }
  private registerChromaKeyShader(): void {
    const AFRAME = (window as any).AFRAME
    if (!AFRAME || AFRAME.shaders["chroma-key-v2"]) return

    AFRAME.registerShader("chroma-key-v2", {
      schema: {
        src:          { type: "map",    is: "uniform" },
        // How far in YCbCr chrominance space from pure green before a pixel is kept
        similarity:   { type: "number", is: "uniform", default: 0.40 },
        // Feather/blend width — pixels between (similarity) and (similarity+smoothness) get partial alpha
        smoothness:   { type: "number", is: "uniform", default: 0.15 },
        // Green spill suppression strength (0 = none, 1 = full) — removes green hue on edge pixels
        spillStrength:{ type: "number", is: "uniform", default: 0.9 }
      },

      vertexShader: [
        "varying vec2 vUv;",
        "void main() {",
        "  vUv = uv;",
        "  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);",
        "}"
      ].join("\n"),

      // YCbCr-space chroma-key with green spill suppression.
      //
      // Two-pass approach:
      //   1. Key: compute chrominance distance from pure green in YCbCr space.
      //      Smoothstep gives a soft feathered edge instead of a hard cut.
      //   2. Spill suppress: for pixels that survived the key but still carry a
      //      green dominant channel (the telltale hue from greenscreen bounce
      //      light), reduce the green channel to the max of red & blue so the
      //      colour cast is neutralised without darkening the pixel.
      fragmentShader: [
        "uniform sampler2D src;",
        "uniform float similarity;",
        "uniform float smoothness;",
        "uniform float spillStrength;",
        "varying vec2 vUv;",

        "// Convert linear RGB to YCbCr (BT.601)",
        "vec3 rgbToYCbCr(vec3 rgb) {",
        "  float y  =  0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;",
        "  float cb = -0.169 * rgb.r - 0.331 * rgb.g + 0.500 * rgb.b + 0.5;",
        "  float cr =  0.500 * rgb.r - 0.419 * rgb.g - 0.081 * rgb.b + 0.5;",
        "  return vec3(y, cb, cr);",
        "}",

        "void main() {",
        "  vec4 color = texture2D(src, vUv);",

        "  // --- 1. CHROMA KEY ---",
        "  // Measure chrominance (Cb/Cr only) distance from pure greenscreen green.",
        "  // Ignoring luma means shadowed/lit areas of the cloth are keyed equally.",
        "  vec3 keyYCbCr = rgbToYCbCr(vec3(0.0, 1.0, 0.0));",
        "  vec3 pixYCbCr = rgbToYCbCr(color.rgb);",
        "  float dist = distance(pixYCbCr.yz, keyYCbCr.yz);",

        "  // Soft edge: fully transparent below similarity, fully opaque above similarity+smoothness",
        "  float alpha = smoothstep(similarity, similarity + smoothness, dist);",

        "  // Early discard for fully transparent pixels (GPU perf)",
        "  if (alpha < 0.01) discard;",

        "  // --- 2. GREEN SPILL SUPPRESSION ---",
        "  // Pixels near the key edge often have a green colour cast from",
        "  // reflected greenscreen light.  Clamp the green channel to",
        "  // max(red, blue) to neutralise the hue without dimming the pixel.",
        "  vec3 deSpilled = color.rgb;",
        "  float maxRB = max(color.r, color.b);",
        "  // spillMask: how much green exceeds the red/blue channels",
        "  float spillMask = max(0.0, color.g - maxRB);",
        "  // Replace excess green with the average of red & blue for a neutral tone",
        "  deSpilled.g -= spillMask * spillStrength;",
        "  deSpilled.g += spillMask * spillStrength * (color.r + color.b) * 0.5;",

        "  // Blend de-spilled colour proportional to how close to the key edge",
        "  // (alpha < 1 = edge pixel = more spill suppression needed)",
        "  vec3 finalColor = mix(deSpilled, color.rgb, alpha);",

        "  gl_FragColor = vec4(finalColor, alpha);",
        "}"
      ].join("\n")
    })

    console.log("AREngine: chroma-key-v2 (greenscreen + spill suppress) shader registered")
  }

  private registerMagentaChromaKeyShader(): void {
    const AFRAME = (window as any).AFRAME
    if (!AFRAME || AFRAME.shaders['chroma-key-magenta-v1']) return

    AFRAME.registerShader('chroma-key-magenta-v1', {
      schema: {
        src:           { type: 'map',    is: 'uniform' },
        // How far in YCbCr chrominance space from pure magenta before a pixel is kept
        similarity:    { type: 'number', is: 'uniform', default: 0.36 },
        // Feather width at keyed edges
        smoothness:    { type: 'number', is: 'uniform', default: 0.10 },
        // Magenta spill suppression strength for edge pixels
        spillStrength: { type: 'number', is: 'uniform', default: 0.8 }
      },

      vertexShader: [
        'varying vec2 vUv;',
        'void main() {',
        '  vUv = uv;',
        '  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);',
        '}'
      ].join('\n'),

      fragmentShader: [
        'uniform sampler2D src;',
        'uniform float similarity;',
        'uniform float smoothness;',
        'uniform float spillStrength;',
        'varying vec2 vUv;',

        '// Convert linear RGB to YCbCr (BT.601)',
        'vec3 rgbToYCbCr(vec3 rgb) {',
        '  float y  =  0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;',
        '  float cb = -0.169 * rgb.r - 0.331 * rgb.g + 0.500 * rgb.b + 0.5;',
        '  float cr =  0.500 * rgb.r - 0.419 * rgb.g - 0.081 * rgb.b + 0.5;',
        '  return vec3(y, cb, cr);',
        '}',

        'void main() {',
        '  vec4 color = texture2D(src, vUv);',

        '  // Key against pure magenta in chrominance space',
        '  vec3 keyYCbCr = rgbToYCbCr(vec3(1.0, 0.0, 1.0));',
        '  vec3 pixYCbCr = rgbToYCbCr(color.rgb);',
        '  float dist = distance(pixYCbCr.yz, keyYCbCr.yz);',
        '  float alpha = smoothstep(similarity, similarity + smoothness, dist);',
        '  if (alpha < 0.01) discard;',

        '  // Suppress magenta spill by reducing red/blue excess over green',
        '  vec3 deSpilled = color.rgb;',
        '  float redSpill = max(0.0, color.r - color.g);',
        '  float blueSpill = max(0.0, color.b - color.g);',
        '  deSpilled.r -= redSpill * spillStrength;',
        '  deSpilled.b -= blueSpill * spillStrength;',

        '  vec3 finalColor = mix(deSpilled, color.rgb, alpha);',
        '  gl_FragColor = vec4(finalColor, alpha);',
        '}'
      ].join('\n')
    })

    console.log('AREngine: chroma-key-magenta-v1 shader registered for magenta backgrounds')
  }

  /**
   * Check AR prerequisites before initialization
   * Requirements: 2.3, 2.4, 2.5
   */
  private async checkARPrerequisites(): Promise<void> {
    // Check if A-Frame is loaded
    if (typeof window.AFRAME === 'undefined') {
      throw new Error('A-Frame library not loaded')
    }

    // Register luma-key shader for iOS Safari transparency support
    //this.registerLumaKeyShader()
    this.registerChromaKeyShader()
    this.registerMagentaChromaKeyShader()
    // Check if MindAR is loaded
    if (typeof (window as any).MINDAR === 'undefined') {
      throw new Error('MindAR library not loaded')
    }

    // Check WebGL support
    if (!this.checkWebGLSupport()) {
      throw new Error('WebGL not supported - required for AR functionality')
    }

    // Check if image target file is accessible.
    // Use the per-character override when set so we validate the actual file
    // that MindAR will load, not the collection-level mind file.
    try {
      const targetToValidate = this.imageTargetOverride ?? this.clientAssetConfig.imageTargetsUrl
      const response = await fetch(targetToValidate, { cache: 'no-store' })
      if (!response.ok) {
        throw new Error(`MindAR target file not found: ${response.status}`)
      }
      
      const arrayBuffer = await response.arrayBuffer()
      if (arrayBuffer.byteLength < 1000) {
        throw new Error('MindAR target file appears to be corrupted or too small')
      }
      
      console.log(`MindAR target file validated: ${targetToValidate} — ${arrayBuffer.byteLength} bytes`)
    } catch (error) {
      throw new Error(`Failed to validate MindAR target file: ${error}`)
    }
  }

  /**
   * Check WebGL support
   */
  private checkWebGLSupport(): boolean {
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      return !!gl
    } catch (error) {
      return false
    }
  }

  /**
   * Clean up partial initialization on error
   */
  private cleanup(): void {
    try {
      if (this.videoElement && this.videoElement.parentNode) {
        this.videoElement.parentNode.removeChild(this.videoElement)
      }
      if (this.scene && this.scene.parentNode) {
        this.scene.parentNode.removeChild(this.scene)
      }
      // Remove any MindAR-injected scanning overlay elements
      const mindArRoots: (HTMLElement | Document)[] = []
      if (this.container) mindArRoots.push(this.container)
      mindArRoots.push(document.body)
      mindArRoots.forEach(root => {
        root.querySelectorAll<HTMLElement>(
          '[class*="mindar-ui"], [id*="mindar"], [class*="mindar-image"]'
        ).forEach(el => el.parentNode?.removeChild(el))
      })
    } catch (error) {
      console.warn('Error during AR scene cleanup:', error)
    }
    
    this.container = null
    this.scene = null
    this.arContent = null
    this.model = null
    this.cta3D = null
    this.videoElement = null
    this.isInitialized = false
    this.isModelLoaded = false
    this.animationState = 'stopped'
    this.isTargetDetected = false
    this.wasAnimationCompleteBeforeLoss = false
    this.wereCTAsVisibleBeforeLoss = false
    this.hasAudioUnlocked = false
    this.removeAudioUnlockGesture()
    this.hideTapForSoundPrompt()
    if (this.audioContext) {
      this.audioContext.close().catch(() => {})
      this.audioContext = null
      this.boostedAudioSource = null
      this.boostedGainNode = null
    }
  }

  /**
   * Wait for A-Frame to initialize with timeout
   */
  private async waitForAFrameInit(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.scene) {
        resolve()
        return
      }

      const timeout = setTimeout(() => {
        reject(new Error('A-Frame initialization timeout'))
      }, 10000) // 10 second timeout

      this.scene.addEventListener('loaded', () => {
        clearTimeout(timeout)
        console.log('A-Frame scene loaded')
        resolve()
      })

      // Also listen for error events
      this.scene.addEventListener('error', (error) => {
        clearTimeout(timeout)
        reject(new Error(`A-Frame initialization error: ${error}`))
      })
    })
  }

  /**
   * Explicitly starts MindAR after A-Frame scene loads.
   *
   * We set autoStart: false on the a-scene so A-Frame's play() does NOT call
   * _startAR automatically.  Without autoStart:false, _startAR runs before
   * start() has created the controller object, causing:
   *   "Cannot read properties of undefined (reading 'dummyRun')"
   *
   * By awaiting mindArSystem.start() here we guarantee:
   *   1. Controller is created + TF.js warmed up (dummyRun succeeds)
   *   2. Camera stream is open and has live frames
   *   3. AR background rendering pipeline is set up
   */
  private async ensureMindARStarted(): Promise<void> {
    if (!this.scene) return

    const sceneEl = this.scene as any
    const systems = sceneEl.systems || {}
    const mindArSystem = systems['mindar-image-system'] || systems['mindar-image']

    if (!mindArSystem || typeof mindArSystem.start !== 'function') {
      console.warn('MindAR system not found after scene load — relying on fallback camera poll')
      await this.waitForCameraVideo()
      return
    }

    try {
      await mindArSystem.start()
      await this.waitForCameraVideo()
      console.log('MindAR camera started successfully (attempt 1)')
    } catch (error) {
      console.error('MindAR start() failed:', error)
      document.dispatchEvent(new CustomEvent('cameraFeedWarning', {
        detail: { message: 'Camera started but AR tracking failed to initialise. Try refreshing.' }
      }))
      throw error
    }
  }

  /**
   * Wait until MindAR's camera video element has a live frame size.
   */
  private async waitForCameraVideo(timeoutMs = 5000): Promise<void> {
    const start = Date.now()

    while (Date.now() - start < timeoutMs) {
      const cameraVideo = this.findMindARCameraVideo()
      if (cameraVideo && cameraVideo.videoWidth > 0 && cameraVideo.videoHeight > 0) {
        if (cameraVideo.paused) {
          cameraVideo.play().catch(() => {})
        }
        document.dispatchEvent(new CustomEvent('cameraFeedHealthy'))
        return
      }
      await new Promise(resolve => setTimeout(resolve, 120))
    }

    document.dispatchEvent(new CustomEvent('cameraFeedWarning', {
      detail: {
        message: 'Camera permission is granted, but no live image is coming through. Move to better light, close other camera apps, or refresh.'
      }
    }))
    console.warn('MindAR camera video did not report live dimensions before timeout')
  }

  /**
   * Locate the MindAR camera video element (excluding experience content videos).
   */
  private findMindARCameraVideo(): HTMLVideoElement | null {
    const videos = Array.from(document.querySelectorAll('video')) as HTMLVideoElement[]
    return videos.find(video => {
      if (video.id === 'roq-video') return false
      const className = (video.className || '').toString()
      const id = (video.id || '').toLowerCase()
      return className.includes('mindar') || id.includes('mindar') || !!video.srcObject
    }) || null
  }

}